import { Link, useParams } from "react-router-dom";
import AccountMenu from "components/account-menu";
import { useContext, useEffect, useState } from "react";
import {
  CiqIconBox,
  MaterialCiqIcon,
  ScheduleCiqIcon,
  SubmittalCiqIcon
} from "components/svg-icons";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import ProjectLogoIcon from "components/svg-icons/project-logo";
import AppWhiteLogoIcon from "components/svg-icons/app-white-logo-icon";
import RightArrowIcon from "components/svg-icons/right-arrow";
import { QUERY_GET_ENTITY_COUNT_V2 } from "services/graphQL/ciq-queries";
import { ProjectPermissionEnum } from "constants/index";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import BidPackageCiqIcon from "components/svg-icons/bid-package-ciq-icon";
import { DownOutlined } from "@ant-design/icons";
import DesignPackageCiqIcon from "components/svg-icons/design-package-ciq-icon";
import { Popover } from "antd";
import { DateTimePanel } from "./navbar";

import styles from "./navbar.module.scss";

function DesignBidCard() {
  const { projectId } = useParams<{ projectId: string }>();

  const content = (
    <div className="flex flex-col bg-ciq-header-100 rounded-lg shadow-xl bg-opacity-100 space-y-1">
      <Link
        to={`/project/${projectId}/design-packages`}
        className="flex items-center px-5 py-3 space-x-2 cursor-pointer hover:bg-black rounded-t-lg"
        data-testid="projectdashboardnavbar-link-coordinate-design-packages"
      >
        <CiqIconBox feature="DESIGN_PACKAGE" size={20} padding={8} />
        <div className="flex flex-col text-white">
          <div className="font-medium">Design</div>
          <div className="text-xs text-white text-opacity-60">
            Coordinate with Design Packages
          </div>
        </div>
      </Link>
      <Link
        to={`/project/${projectId}/bid-packages`}
        className="flex items-center px-5 py-3 space-x-2 cursor-pointer hover:bg-black rounded-b-lg"
        data-testid="projectdashboardnavbar-link-coordinate-buyouts"
      >
        <CiqIconBox feature="BID_PACKAGE" size={20} padding={8} />
        <div className="flex flex-col text-white">
          <div className="font-medium">Preconstruction</div>
          <div className="text-xs text-white text-opacity-60">
            Coordinate Buyouts
          </div>
        </div>
      </Link>
    </div>
  );
  return (
    <Popover
      content={content}
      overlayClassName={styles.popoverNavbarNoPadding}
      placement="bottomRight"
      showArrow={false}
    >
      <div className="relative cursor-pointer flex w-44 h-12 rounded border border-solid border-ciq-primary-300 bg-ciq-primary-400 text-white hover:text-white">
        <div
          className="absolute right-0 top-0 px-2 py-0.5 rounded-bl rounded-tr border-solid border-0 border-b border-l border-ciq-primary-900 bg-ciq-primary-900 bg-opacity-60"
          style={{ fontSize: 10 }}
        >
          Demo
        </div>
        <div className="absolute right-1.5 bottom-0">
          <DownOutlined className="text-white" />
        </div>

        <div
          className={`relative w-10 shrink-0 h-full rounded-l ${styles.ciqBoxGradient}`}
        >
          <div className="absolute left-0 top-0 w-3/4 h-3/4 flex justify-center items-center">
            <DesignPackageCiqIcon
              size={16}
              className="fill-ciq-golden-purple-6"
              opacity="1"
            />
          </div>
          <div className="absolute right-0 bottom-0 w-3/4 h-3/4 flex justify-center items-center">
            <BidPackageCiqIcon
              size={16}
              className="fill-ciq-blue-250"
              opacity="1"
            />
          </div>
        </div>
        <div className="flex font-medium text-xs items-center pl-2">
          <div>Design & Preconstruction</div>
        </div>
      </div>
    </Popover>
  );
}

function LogCard({
  color,
  title,
  count,
  to,
  icon
}: {
  color: string;
  title: string;
  count: number | null;
  to: string;
  icon: JSX.Element;
}) {
  return (
    <Link
      to={to}
      className="relative flex w-40 h-12 shrink-0 rounded border border-solid border-ciq-primary-300 bg-ciq-primary-400 text-white hover:text-white"
      data-testid="projectdashboardnavbar-link-logcard"
    >
      <div className="absolute right-1 bottom-0">
        <RightArrowIcon />
      </div>
      <div
        className="flex justify-center items-center w-10 h-full rounded-l"
        style={{ backgroundColor: color }}
      >
        {icon}
      </div>
      <div className="flex flex-col p-1 pl-2">
        <div className="font-medium">{title}</div>
        <div className="font-bold text-xs" style={{ color }}>
          {count}
        </div>
      </div>
    </Link>
  );
}

function SmallCard({ name, to }: { name: string; to: string }) {
  return (
    <Link
      to={to}
      className="flex justify-between border border-solid border-ciq-primary-300 bg-ciq-primary-400 rounded px-2 py-0.5 text-white hover:text-white text-xs font-medium"
      data-testid="projectdashboardnavbar-link-smallcard"
    >
      <div className="pl-1">{name}</div>
      <RightArrowIcon />
    </Link>
  );
}

function ProjectDashboardNavBar() {
  const { projectId } = useParams<{ projectId: string }>();
  const {
    projectDetails,
    gqlClientForProject,
    tokenContents,
    isFeatureFlagEnabled
  } = useContext(ProjectContext);

  const [entityCount, setEntityCount] = useState({
    submittals: null,
    materials: null,
    schedules: null
  });

  const isBuyoutFeatureEnabled = isFeatureFlagEnabled("BUYOUT");
  const cannotViewDesignPage = isPermissionNotGrantted(
    ProjectPermissionEnum.ViewDesignPage,
    tokenContents?.role!
  );

  const cannotViewProjectReports = isPermissionNotGrantted(
    ProjectPermissionEnum.ViewProjectReports,
    tokenContents?.role!
  );

  const { data: entityCountData } = useCIQQuery(QUERY_GET_ENTITY_COUNT_V2, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  useEffect(() => {
    if (entityCountData) {
      const submittals =
        entityCountData?.submittals_aggregate?.aggregate?.count;
      const materials = entityCountData?.material_aggregate?.aggregate?.count;
      const schedules = entityCountData.schedule_import_details?.length;
      setEntityCount({ submittals, materials, schedules });
    }
  }, [entityCountData]);

  return (
    <div className="flex flex-col w-full bg-ciq-primary-200 text-white hover:text-white p-4">
      <div className="flex justify-between">
        <Link
          to="/projects"
          className="flex"
          data-testid="projectdashboardnavbar-link-app-logo"
        >
          <div className="w-[120px] flex">
            <AppWhiteLogoIcon />
          </div>
        </Link>
        <div className="flex space-x-4 items-center cursor-pointer">
          <AccountMenu />
        </div>
      </div>
      <div className="flex px-7 py-2 justify-between">
        <Link
          to={`/project/${projectId}/dashboard`}
          className="flex space-x-2 text-white hover:text-white"
          data-testid="projectdashboardnavbar-link-project-logo"
        >
          <ProjectLogoIcon />
          {projectDetails?.id && (
            <div className="flex flex-col">
              <div className="text-xl font-semibold truncate max-w-md">
                <TruncatedTextWithTooltip text={projectDetails?.name} />
              </div>
              <DateTimePanel />
            </div>
          )}
        </Link>
        <div className="flex items-center space-x-4">
          {isBuyoutFeatureEnabled && !cannotViewDesignPage && <DesignBidCard />}
          <LogCard
            color="var(--ciq-primary-900-light)"
            title="Submittals"
            count={entityCount.submittals}
            to={`/project/${projectId}/submittals`}
            icon={
              <SubmittalCiqIcon
                size={23}
                className="fill-ciq-primary-900"
                opacity="1"
              />
            }
          />
          <LogCard
            color="var(--ciq-blue-250)"
            title="Materials"
            count={entityCount.materials}
            to={`/project/${projectId}/materials`}
            icon={
              <MaterialCiqIcon
                size={23}
                className="fill-ciq-blue-400"
                opacity="1"
              />
            }
          />
          <LogCard
            color="var(--ciq-orange-870)"
            title="Schedules"
            count={entityCount.schedules}
            to={`/project/${projectId}/schedule/project-schedules`}
            icon={
              <ScheduleCiqIcon
                size={23}
                className="fill-ciq-orange-890"
                opacity="1"
              />
            }
          />
          <div className="flex flex-col-reverse justify-between w-28 h-12">
            <SmallCard name="Settings" to={`/project/${projectId}/settings`} />
            {!cannotViewProjectReports && (
              <SmallCard name="Reports" to={`/project/${projectId}/reports`} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectDashboardNavBar;
